import { FunctionComponent, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import { parseSectionItem } from '@wbk/contentful';
import { Zone } from '@wbk/contentful/api';
import { ImageWithPlaceholder } from '@wbk/ui';
import useLocalization from '@/context/localization/useLocalization';
import { Carousel } from '@/components/ui/Carousel';
import Directions from './directions';
import Frame from './frame';

export type BentoType = {
  zone: Zone;
  className?: string;
};

const Bento: FunctionComponent<BentoType> = ({ zone, className = '' }) => {
  const { locale } = useLocalization();
  const { slug } = useParams();

  const { event, experience, restaurant, whenToVisit } = useMemo(() => {
    const bentoData = zone?.webSectionsCollection?.items
      ?.filter((y) => y?.__typename === 'WebSection')
      ?.find((x) => x?.renderType === 'hero-grid');

    const collections = (bentoData?.contentCollection?.items ?? []).map((item) =>
      parseSectionItem({ item, locale })
    );

    const event = collections.find((x) => x?.id === 'Events');
    const experience = collections.find((x) => x?.id === 'Experiences');
    const restaurant = collections.find((x) => x?.id === 'Restaurants');
    const whenToVisit = collections.find((x) => x?.id === 'When to visit?');

    return {
      event,
      experience,
      restaurant,
      whenToVisit,
    };
  }, [zone, locale]);

  const frameItems = useMemo(
    () => [
      ...(event
        ? [
            {
              iconCalendar: '/images/illustration/zones/Icon-Calendar@2x.png',
              events: event?.title,
              upcomingEvents: event?.subtitle,
              presentedBy: '/images/illustration/zones/Presented-by.svg',
              frameBackgroundColor: '#00d2b0',
              href: '#events',
            },
          ]
        : []),
      ...(experience
        ? [
            {
              iconCalendar: '/images/illustration/zones/Icon-Media-Add-music@2x.png',
              events: experience?.title,
              upcomingEvents: experience?.subtitle,
              presentedBy: '/images/illustration/zones/Presented-by.svg',
              frameBackgroundColor: '#ff9b27',
              href: `/${locale}/explore?zone=${slug}`,
            },
          ]
        : []),
      ...(restaurant
        ? [
            {
              iconCalendar: '/images/illustration/zones/Icon-Cooking-Knife-fork-2.svg',
              events: restaurant?.title,
              upcomingEvents: restaurant?.subtitle,
              presentedBy: '/images/illustration/zones/Presented-by.svg',
              frameBackgroundColor: '#987bff',
              href: restaurant?.href,
              target: '_blank',
            },
          ]
        : []),
    ],
    [event, experience, restaurant]
  );

  return (
    <section className='container-padding container lg:space-y-12'>
      <section
        className={`container flex max-w-full flex-1 flex-col items-start gap-2 self-stretch overflow-hidden px-3 sm:gap-6 xl:px-0 ${className}`}
      >
        <section
          key={locale}
          className={twMerge(
            'grid grid-cols-2 gap-2 self-stretch text-left text-2xl sm:gap-6 lg:grid-cols-3',
            frameItems.length < 3 && 'lg:grid-cols-2',
            frameItems.length < 2 && 'grid-cols-1 lg:grid-cols-1'
          )}
        >
          {frameItems.map((item, index) => (
            <Frame
              key={index}
              className={twMerge(
                index === 0 ? 'col-span-2 lg:col-span-1' : '',
                frameItems.length <= 2 && 'col-span-1'
              )}
              iconCalendar={item.iconCalendar}
              events={item.events}
              upcomingEvents={item.upcomingEvents}
              presentedBy={item.presentedBy}
              frameBackgroundColor={item.frameBackgroundColor}
              href={item.href}
              target={item.target}
            />
          ))}
        </section>
        <main className='flex max-w-full flex-col flex-wrap items-start gap-2 self-stretch overflow-hidden sm:gap-6 lg:flex-row'>
          <section className='order-2 flex w-full max-w-full flex-1 flex-col items-start justify-center gap-2 self-stretch text-left font-[Inter] text-[7.2px] sm:gap-6 lg:order-none lg:w-[410px] lg:flex-none'>
            <div className='bg-primary flex flex-col items-start justify-center self-stretch overflow-hidden rounded-2xl !p-6 [backdrop-filter:blur(8px)]'>
              <div className='relative flex flex-col items-start justify-center gap-3 self-stretch'>
                <img
                  className='relative z-[1] max-h-full w-8 sm:w-10 lg:w-12'
                  alt=''
                  src='/images/illustration/zones/Icon-Home-Clock.svg'
                />
                <div className='z-[2] flex flex-col items-start gap-2 text-2xl'>
                  <h3 className='text-body-M sm:text-body-L relative !m-0 font-[inherit] font-semibold leading-[134%] tracking-[-0.5px] md:leading-[26px]'>
                    {whenToVisit?.title}
                  </h3>
                  <div className='relative flex w-full items-end text-left text-[13px] leading-[16px] rtl:text-right'>
                    {whenToVisit?.subtitle}
                  </div>
                </div>
                <div className='z-[3] flex flex-col items-start gap-2 self-stretch text-[13px]'>
                  <div className='border-Transparent-10-Light relative box-border h-px self-stretch border-t-[1px] border-solid' />
                  <div className='relative'>
                    {/* @ts-expect-error -- description is available but not typed */}
                    {whenToVisit?.description}
                  </div>
                </div>
              </div>
            </div>
            <Directions className='hidden lg:block' locationData={zone.location} />
          </section>

          <Carousel
            rootClassName='lg:h-[480px] w-full lg:flex-1 overflow-hidden rounded-2xl'
            containerClassName='h-[42vw] lg:h-full'
            options={{ loop: true }}
            arrows='middle'
            autoplay={{ delay: 5000, stopOnInteraction: false }}
            dots
            className='order-1 object-cover lg:order-none'
          >
            {zone.carousalCollection.items.map((slide) => (
              <div className='relative min-w-0 flex-[0_0_100%]' key={slide.url}>
                <ImageWithPlaceholder
                  className='max-h-[30rem] w-full object-cover'
                  src={slide.url}
                  loading='lazy'
                />
              </div>
            ))}
          </Carousel>
          <Directions
            className='order-3 min-h-[36.32vw] lg:order-none lg:hidden'
            locationData={zone.location}
          />
        </main>
      </section>
    </section>
  );
};

export default Bento;
